import { sessionRecord } from "@buzzeasy/shared-frontend-utilities/sessionRecording";
import jwtDecode from "jwt-decode";
import { PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useEffectEvent } from "react";
import { useSelector } from "react-redux";
import environmentConfig from "../../../environmentConfig";
import { useLoginMutation } from "../../../redux/apis/blenderApi";
import { selectAuthInfo } from "../../../redux/authSlice";
import AppConflictScreen from "../../presenters/AppConfilctScreen";
import FullScreenSpinner from "../../presenters/FullScreenSpinner";
import { UserManagerContext } from "./UserManagerContext";

const { environment, buildNumber, crmMode, isInIframe, isPopupWindow } = environmentConfig;

/**
 * Logs into BlenderGara upon launching the app.
 *
 * **Provider dependencies:**
 * - (Store) Provider
 */

export default function BlenderLoginHandler({ children }: PropsWithChildren): ReactElement {
  const userManager = useContext(UserManagerContext);
  const { token, tenantId, user } = useSelector(selectAuthInfo);

  const [loginToBlender, { data, isLoading }] = useLoginMutation();

  const isLoggedIn = !!data && !isLoading && data.error !== "MobileSessionInProgress";

  useEffect(
    () => { void loginToBlender(); },
    [loginToBlender],
  );

  const recordUserInfo = useEffectEvent(() => {
    sessionRecord.recordCustomEvent(
      "session/userInfo",
      {
        userId: user.id,
        emailAddress: user.email,
        tenantId,
        token: tryDecodeToken(token),
        agentUiDetails: {
          environment,
          buildId: buildNumber,
          fragmentVersions: __BUZZEASY_PACKAGE_VERSIONS__,
          crmMode,
          isInIframe,
          isPopupWindow,
        },
      },
      { permanent: true },
    );
  });

  useEffect(
    () => {
      if (isLoggedIn)
        recordUserInfo();
    },
    [isLoggedIn],
  );

  const logout = useCallback(
    () => {
      void userManager.signoutRedirect();
    },
    [userManager],
  );

  if (!data || isLoading)
    return <FullScreenSpinner />;

  if (data.error === "MobileSessionInProgress")
    return <AppConflictScreen
      onRefresh={() => loginToBlender()}
      onSwitchAccount={logout}
    />;

  return <>{children}</>;
}

function tryDecodeToken(token: string): unknown {
  try {
    return jwtDecode(token);
  }
  catch {
    return null;
  }
}
